import { uuidv7 } from 'uuidv7';

export interface FileSectionLimits {
  maxFileSize: string;
  allowedExtensions?: string[];
}

export interface AppConfig {
  oauthIssuerURL: string;
  oauthClientID: string;
  oauthRedirectURI: string;
  istockURL: string;
  notificationsPollIntervalSeconds: number;
  fileSections?: Record<string, FileSectionLimits>;
}

let cached: AppConfig | null = null;

/** Ошибка загрузки публичного конфига при старте SPA; message без сырых ответов сервера. */
export class ConfigLoadError extends Error {
  readonly httpStatus?: number;

  constructor(message: string, httpStatus?: number) {
    super(message);
    this.name = 'ConfigLoadError';
    this.httpStatus = httpStatus;
  }
}

function userFacingMessageForHttpStatus(status: number): string {
  if (status === 401 || status === 403) {
    return 'Не удалось загрузить настройки. Нажмите «Повторить» или войдите снова.';
  }
  if (status >= 500) {
    return 'Сервер временно недоступен. Попробуйте позже или нажмите «Повторить».';
  }
  return 'Не удалось загрузить настройки приложения. Нажмите «Повторить».';
}

/** Загружает конфиг с бэкенда (GET /api/config). Вызывать один раз при старте приложения. */
export async function loadConfig(): Promise<AppConfig> {
  let resp: Response;
  try {
    resp = await fetch('/api/config', {
      headers: { 'X-Request-Id': uuidv7() },
    });
  } catch {
    throw new ConfigLoadError(
      'Нет соединения с сервером. Проверьте сеть и нажмите «Повторить».',
    );
  }
  if (!resp.ok) {
    const base = userFacingMessageForHttpStatus(resp.status);
    const message = import.meta.env.DEV ? `${base} (${resp.status})` : base;
    throw new ConfigLoadError(message, resp.status);
  }
  cached = (await resp.json()) as AppConfig;
  return cached;
}

/** Возвращает закэшированный конфиг. Требует предварительного вызова loadConfig(). */
export function getConfig(): AppConfig {
  if (!cached) {
    throw new Error('App config is not loaded. Call loadConfig() first.');
  }
  return cached;
}
